import { type Flight, type FlightLookupDateKind, type FlightLookupResult, type FlightLookupRole, type FlightLookupRoute, type TravelDirection, parseFlightDesignator } from "@seagames/contracts";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError } from "../../lib/api/client";
import { flightDatesOptions, lookupFlight } from "../../lib/api/flights";
import { gamesDetailOptions } from "../../lib/competition/games";
import { type FlightDatesMissReason, type FlightDatesOutcome, designatorKey } from "./model";

/**
 * `games.timezone` (IANA, e.g. `Asia/Kuala_Lumpur`) — the zone travel dates
 * and times are entered and shown in. From `GET /games/:id` (cached long; any
 * role that registers people can read it). Undefined while loading: callers
 * hold the leg form until it is known, so no time is ever read in a guessed zone.
 */
export function useGamesTimeZone(gamesId: string | null | undefined): string | undefined {
	const q = useQuery({ ...gamesDetailOptions(gamesId ?? ""), enabled: !!gamesId });
	return q.data?.timezone;
}

/**
 * What the flight-number field says under itself:
 *   idle        nothing asked yet (or the stored flight is shown instead)
 *   loading     asking the API
 *   found       the flight, already copied into the leg
 *   miss        quiet: type the time yourself — tracking off, not found, not via
 *               a Games airport, provider down, too many lookups, no date yet,
 *               not a flight number
 */
export type FlightLookupState =
	| { kind: "idle" }
	| { kind: "loading" }
	| { kind: "found"; flight: Flight }
	| {
			kind: "miss";
			reason: "TRACKING_OFF" | "NOT_FOUND" | "NOT_OUR_AIRPORT" | "UNAVAILABLE" | "RATE_LIMITED" | "NEED_DATE" | "UNREADABLE";
			/** What was asked, when something was: a LEG miss is about a typed date, a SERVICE miss about a picked day. */
			asked?: { date: string; dateKind: FlightLookupDateKind };
			/** NOT_OUR_AIRPORT: where the flight goes, and our airports. */
			route?: FlightLookupRoute;
			airports?: readonly string[];
	  };

/** What one lookup ended in: the answer (null on 503 / 429) and the state it left. Null from `lookup` = not asked, or superseded. */
export interface LookupAnswer {
	result: FlightLookupResult | null;
	state: FlightLookupState;
}

/**
 * One lookup per (flight, date, date kind, direction): blurring the field twice, or
 * pressing Enter after a blur, does not ask again — every call that reaches
 * the provider counts against the login's hourly limit (`FLIGHT_LOOKUP_MAX`).
 * A failed call (503, 429) forgets its key so the next blur may retry.
 * Tracking is an accelerator, never a gate: every miss leaves the fields
 * editable and the save allowed. `role` CONNECTION looks up a transit leg's
 * feeder / onward flight (no Games airport required).
 */
export function useFlightLookup(direction: TravelDirection, role: FlightLookupRole = "MAIN") {
	const [state, setState] = useState<FlightLookupState>({ kind: "idle" });
	const lastKey = useRef<string | null>(null);
	const inFlight = useRef<AbortController | null>(null);

	useEffect(() => () => inFlight.current?.abort(), []);

	const lookup = useCallback(
		async (typed: string, date: string, dateKind: FlightLookupDateKind = "LEG"): Promise<LookupAnswer | null> => {
			const text = typed.trim();
			if (!text) {
				lastKey.current = null;
				setState({ kind: "idle" });
				return null;
			}
			const designator = parseFlightDesignator(text);
			if (!designator) {
				setState({ kind: "miss", reason: "UNREADABLE" });
				return null;
			}
			if (!date) {
				setState({ kind: "miss", reason: "NEED_DATE" });
				return null;
			}
			const flight = `${designator.carrier_code}${designator.flight_number}`;
			const key = `${flight}|${date}|${dateKind}|${direction}|${role}`;
			if (key === lastKey.current) return null;
			lastKey.current = key;
			inFlight.current?.abort();
			const ctrl = new AbortController();
			inFlight.current = ctrl;
			setState({ kind: "loading" });
			try {
				const res = await lookupFlight({ flight, date, direction, ...(dateKind === "SERVICE" ? { date_kind: dateKind } : {}), ...(role === "CONNECTION" ? { role } : {}) }, ctrl.signal);
				if (ctrl.signal.aborted) return null;
				const next: FlightLookupState = res.found && res.flight ? { kind: "found", flight: res.flight } : {
								kind: "miss",
								reason: res.reason ?? "NOT_FOUND",
								asked: { date, dateKind },
								...(res.route ? { route: res.route } : {}),
								...(res.airports ? { airports: res.airports } : {}),
							};
				setState(next);
				return { result: res, state: next };
			} catch (err) {
				if (ctrl.signal.aborted) return null;
				lastKey.current = null;
				const next: FlightLookupState = { kind: "miss", reason: err instanceof ApiError && err.code === "RATE_LIMITED" ? "RATE_LIMITED" : "UNAVAILABLE", asked: { date, dateKind } };
				setState(next);
				return { result: null, state: next };
			} finally {
				if (inFlight.current === ctrl) inFlight.current = null;
			}
		},
		[direction, role],
	);

	/** The flight text changed: whatever was found no longer describes it. */
	const reset = useCallback(() => {
		inFlight.current?.abort();
		inFlight.current = null;
		lastKey.current = null;
		setState({ kind: "idle" });
	}, []);

	return { state, lookup, reset };
}

/**
 * The days a flight number flies (`GET /flights/dates`), asked only for a
 * number the person has committed — blurred or entered, never per keystroke
 * — and only when it parses as a flight designator. `commit(text)` marks the
 * text in the field as committed; typing something else makes it idle again
 * until the next commit. Cached per (compact designator, direction) for an
 * hour, so going back to a number asks nothing. A failure maps by
 * `error.code`: RATE_LIMITED → RATE_LIMITED, anything else → UNAVAILABLE —
 * both a fallback to typed dates; the next commit of the same number retries.
 * `initialCommit`: a number committed from the start (a transit row seeded
 * from a number already entered).
 */
export function useFlightDates(direction: TravelDirection, serviceNo: string, initialCommit: string | null = null) {
	const [committedText, setCommittedText] = useState<string | null>(initialCommit);
	const committed = committedText !== null && committedText.trim() !== "" && designatorKey(committedText) === designatorKey(serviceNo);
	const parsed = committed ? parseFlightDesignator(serviceNo.trim()) : null;
	const flight = parsed ? `${parsed.carrier_code}${parsed.flight_number}` : "";
	const q = useQuery({ ...flightDatesOptions(flight, direction), enabled: flight !== "" });

	let outcome: FlightDatesOutcome;
	if (!flight) outcome = { kind: "idle" };
	else if (q.isPending || (q.isError && q.isFetching)) outcome = { kind: "loading" };
	else if (q.isError) outcome = { kind: "miss", reason: datesErrorReason(q.error) };
	else {
		const window = { from: q.data.from, to: q.data.to };
		if (q.data.found) outcome = { kind: "found", dates: q.data.dates, window };
		// The dates endpoint never answers NOT_OUR_AIRPORT (it knows no routes); read it as NOT_FOUND.
		else outcome = { kind: "miss", reason: q.data.reason === "TRACKING_OFF" ? "TRACKING_OFF" : "NOT_FOUND", window };
	}

	const { isError, refetch } = q;
	const commit = useCallback(
		(text: string) => {
			setCommittedText(text);
			// Same number again after a 503 / 429: one more try.
			if (isError && designatorKey(text) === flight) void refetch();
		},
		[isError, refetch, flight],
	);

	return { outcome, committed, commit };
}

const datesErrorReason = (err: unknown): FlightDatesMissReason => (err instanceof ApiError && err.code === "RATE_LIMITED" ? "RATE_LIMITED" : "UNAVAILABLE");
